import { useEffect, useMemo, useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Container,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useApp } from "../context/AuthContext";
import logo from "../static/media/logo.webp";
import NavbarMobile from "./Navbar/NavbarMobile";

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, isAdmin, authLoading, logout } = useAuth();
  const { getCartCount } = useApp();

  const [scrolled, setScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 24);
    };

    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setAnchorEl(null);
  }, [location.pathname]);

  const navItems = useMemo(
    () => [
      { label: "Inicio", to: "/" },
      { label: "Acerca de nosotros", to: "/acerca-de-nosotros" },
      { label: "Tienda", to: "/tienda" },
      { label: "Entrena mejor", to: "/entrenamiento" },
      { label: "Aliados", to: "/nuestros-aliados" },
    ],
    []
  );

  const cartCount = getCartCount();
  const isActive = (path) => location.pathname === path;

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleMenuClose();
    navigate("/");
  };

  const handleAccountClick = () => {
    handleMenuClose();
    navigate(isAdmin ? "/admin/dashboard/staff" : "/mi-perfil");
  };

  const handleGoCheckout = () => {
    navigate("/checkout");
  };

  const profilePhoto = user?.profile?.profile_photo || "";

  return (
    <>
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        <NavbarMobile
          scrolled={scrolled}
          navItems={navItems}
          isActive={isActive}
          authLoading={authLoading}
          isAuthenticated={isAuthenticated}
          isAdmin={isAdmin}
          profilePhoto={profilePhoto}
          cartCount={cartCount}
          handleLogout={handleLogout}
          handleGoCheckout={handleGoCheckout}
        />
      </Box>

      <Box sx={{ display: { xs: "none", md: "block" } }}>
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: "rgba(255,255,255,0.94)",
            backdropFilter: "blur(14px)",
            color: "#111111",
            borderBottom: scrolled
              ? "1px solid rgba(17,17,17,0.08)"
              : "1px solid rgba(17,17,17,0.05)",
            boxShadow: scrolled
              ? "0 18px 38px rgba(0,0,0,0.08)"
              : "0 10px 28px rgba(0,0,0,0.04)",
            transition:
              "box-shadow .28s ease, border-color .28s ease, background-color .28s ease",
          }}
        >
          <Box
            sx={{
              height: scrolled ? 0 : 42,
              overflow: "hidden",
              bgcolor: "#111111",
              color: "#ffffff",
              borderBottom: scrolled
                ? "0 solid transparent"
                : "1px solid rgba(255,255,255,0.08)",
              transition:
                "height .28s ease, opacity .28s ease, border-color .28s ease",
              opacity: scrolled ? 0 : 1,
            }}
          >
            <Container maxWidth="xl" sx={{ height: "100%" }}>
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Stack direction="row" spacing={3} sx={{ alignItems: "center" }}>
                  <Box
                    component="a"
                    href="https://wa.me/593985224301"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.8,
                      textDecoration: "none",
                      color: "#ffffff",
                      fontSize: 13.4,
                      fontWeight: 600,
                      "&:hover": {
                        color: "#ff4d57",
                      },
                    }}
                  >
                    <WhatsAppIcon sx={{ fontSize: 16.5 }} />
                    <Typography component="span" sx={{ fontSize: 13.4, fontWeight: 700 }}>
                      0985224301
                    </Typography>
                  </Box>

                  <Box
                    component="a"
                    href="mailto:inscripciones@nova-xfit.com"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.8,
                      textDecoration: "none",
                      color: "#ffffff",
                      fontSize: 13.4,
                      fontWeight: 600,
                      "&:hover": {
                        color: "#ff4d57",
                      },
                    }}
                  >
                    <EmailRoundedIcon sx={{ fontSize: 16.5 }} />
                    <Typography component="span" sx={{ fontSize: 13.4, fontWeight: 700 }}>
                      inscripciones@nova-xfit.com
                    </Typography>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                  <IconButton
                    onClick={handleGoCheckout}
                    aria-label="Carrito"
                    title="Carrito"
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 999,
                      color: "#ffffff",
                      border: "1px solid rgba(255,255,255,0.16)",
                      "&:hover": {
                        color: "#ff4d57",
                        bgcolor: "rgba(255,77,87,0.08)",
                        borderColor: "rgba(255,77,87,0.45)",
                      },
                    }}
                  >
                    <Badge
                      badgeContent={cartCount}
                      color="error"
                      overlap="circular"
                      max={99}
                    >
                      <ShoppingCartOutlinedIcon />
                    </Badge>
                  </IconButton>

                  {authLoading ? (
                    <Box sx={{ width: 110, height: 34 }} />
                  ) : !isAuthenticated ? (
                    <Stack direction="row" spacing={1}>
                      <Button
                        component={RouterLink}
                        to="/login"
                        sx={{
                          minWidth: "auto",
                          px: 1.4,
                          py: 0.5,
                          borderRadius: 999,
                          textTransform: "none",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#ffffff",
                          "&:hover": {
                            color: "#ff4d57",
                            bgcolor: "transparent",
                          },
                        }}
                      >
                        Iniciar sesión
                      </Button>

                      <Button
                        component={RouterLink}
                        to="/registro"
                        sx={{
                          minWidth: "auto",
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 999,
                          textTransform: "none",
                          fontSize: 13,
                          fontWeight: 600,
                          color: "#ffffff",
                          border: "1px solid rgba(255,255,255,0.16)",
                          "&:hover": {
                            color: "#ff4d57",
                            borderColor: "rgba(255,77,87,0.45)",
                            bgcolor: "rgba(255,77,87,0.08)",
                          },
                        }}
                      >
                        Registrarse
                      </Button>
                    </Stack>
                  ) : (
                    <IconButton
                      onClick={handleMenuOpen}
                      aria-label="Abrir menú de cuenta"
                      sx={{
                        p: 0.25,
                        border: "1px solid rgba(255,255,255,0.14)",
                      }}
                    >
                      <Avatar
                        src={profilePhoto}
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: "#1f1f1f",
                        }}
                      >
                        {!profilePhoto && <PersonRoundedIcon sx={{ fontSize: 18 }} />}
                      </Avatar>
                    </IconButton>
                  )}
                </Stack>
              </Box>
            </Container>
          </Box>

          <Container maxWidth="xl">
            <Toolbar
              disableGutters
              sx={{
                minHeight: scrolled ? 74 : 84,
                transition: "min-height .28s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box
                component={RouterLink}
                to="/"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  textDecoration: "none",
                  flexShrink: 0,
                  mr: 1,
                }}
              >
                <Box
                  component="img"
                  src={logo}
                  alt="NovaXFit"
                  sx={{
                    height: scrolled ? 48 : 58,
                    width: "auto",
                    display: "block",
                    transition: "height .28s ease",
                  }}
                />
              </Box>

              <Stack
                direction="row"
                spacing={0.8}
                sx={{
                  alignItems: "center",
                  flex: 1,
                  justifyContent: "center",
                }}
              >
                {navItems.map((item) => {
                  const active = isActive(item.to);

                  return (
                    <Button
                      key={item.label}
                      component={RouterLink}
                      to={item.to}
                      disableElevation
                      sx={{
                        minWidth: "auto",
                        px: 1.5,
                        py: 0.95,
                        textTransform: "none",
                        fontSize: 14.8,
                        fontWeight: 700,
                        color: active ? "#6b000a" : "#111111",
                        bgcolor: "transparent",
                        borderRadius: 999,
                        position: "relative",
                        transition:
                          "color .22s ease, background-color .22s ease, transform .22s ease",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          left: 12,
                          right: 12,
                          bottom: 6,
                          height: 2,
                          borderRadius: 999,
                          bgcolor: active ? "#6b000a" : "transparent",
                          transition: "background-color .22s ease",
                        },
                        "&:hover": {
                          color: "#6b000a",
                          bgcolor: "rgba(17,17,17,0.04)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      {item.label}
                    </Button>
                  );
                })}

                <Button
                  component={RouterLink}
                  to="/contacto"
                  disableElevation
                  sx={{
                    minWidth: "auto",
                    ml: 0.6,
                    px: 2.1,
                    py: 0.92,
                    borderRadius: 999,
                    textTransform: "none",
                    fontSize: 14.5,
                    fontWeight: 700,
                    color: "#ffffff",
                    background:
                      "linear-gradient(135deg, #6b000a 0%, #b30f1d 42%, #e32735 72%, #111111 100%)",
                    border: "1px solid rgba(0,0,0,0.10)",
                    boxShadow:
                      "inset 0 1px 0 rgba(255,255,255,0.18), 0 10px 24px rgba(180,20,30,0.20)",
                    "&:hover": {
                      filter: "brightness(1.05)",
                      boxShadow:
                        "inset 0 1px 0 rgba(255,255,255,0.18), 0 12px 26px rgba(180,20,30,0.24)",
                    },
                  }}
                >
                  Contacto
                </Button>
              </Stack>

              <Box
                sx={{
                  minWidth: 24,
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                }}
              />
            </Toolbar>
          </Container>
        </AppBar>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: 3,
              minWidth: 190,
              boxShadow: "0 18px 40px rgba(0,0,0,0.12)",
            },
          }}
        >
          <MenuItem onClick={handleAccountClick} sx={{ fontWeight: 700 }}>
            {isAdmin ? "Dashboard" : "Mi perfil"}
          </MenuItem>
          <MenuItem onClick={handleLogout} sx={{ fontWeight: 600, color: "#e50914" }}>
            Cerrar sesión
          </MenuItem>
        </Menu>
      </Box>
    </>
  );
}